import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate } from '../utils/dateFormatter';
import './GraphicRecapMinimal.css';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicRecapMinimal({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dateFormat = 'full',
}) {
  const {
    homeTeam,
    homeScore,
    awayTeam,
    awayScore,
    gameDate,
    topPerformer,
    topPerformerStats,
  } = graphicData;

  const homeWon = parseInt(homeScore) > parseInt(awayScore);
  const isTie = parseInt(homeScore) === parseInt(awayScore);

  const getBackgroundStyle = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 0%, ${teamColors.primary} 50%, ${teamColors.secondary} 50%, ${teamColors.secondary} 100%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const isLightColor = (color) => {
    if (!color || color === 'null') return false; // Default to dark if color is null/undefined
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  const getTextColor = () => {
    const primaryLight = isLightColor(teamColors.primary);
    const secondaryLight = isLightColor(teamColors.secondary);

    if (primaryLight && secondaryLight) {
      return '#2d3748';
    }
    if (primaryLight) {
      return '#374151';
    }
    return '#ffffff';
  };

  const getTextShadow = () => {
    const textColor = getTextColor();
    if (textColor !== '#ffffff') {
      return '1px 1px 2px rgba(255, 255, 255, 0.3)';
    }
    return '3px 3px 6px rgba(0, 0, 0, 0.3)';
  };

  const textColor = getTextColor();
  const textShadow = getTextShadow();

  return (
    <div
      className="graphic-recap-minimal"
      id="graphic-recap"
      style={{
        background: getBackgroundStyle(),
        color: textColor,
      }}
    >
      {/* Top right logo */}
      <div className="recap-minimal-top-logo">
        {homeTeamLogo ? (
          <div className="team-logo-circle team-logo-circle-md">
            <img
              src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
              alt={homeTeam}
              className="recap-minimal-top-logo-img"
            />
          </div>
        ) : (
          <div className="team-logo-circle team-logo-circle-md">
            <div className="recap-minimal-top-logo-placeholder">
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </div>
          </div>
        )}
      </div>

      {/* Content overlay */}
      <div className="recap-minimal-content">
        <div className="recap-minimal-badge">
          {isTie ? 'TIE GAME' : homeWon ? 'VICTORY!' : 'FINAL SCORE'}
        </div>

        <div className="recap-minimal-score">
          <div className="recap-minimal-score-display">
            {homeScore || '0'} - {awayScore || '0'}
          </div>
        </div>

        <div className="recap-minimal-teams">
          <div className="recap-minimal-team-name" style={{ textShadow }}>
            {homeTeam || 'YOUR TEAM'}
          </div>
          <div className="recap-minimal-vs">VS</div>
          <div className="recap-minimal-team-name" style={{ textShadow }}>
            {awayTeam || 'OPPONENT'}
          </div>
        </div>

        {topPerformer && (
          <div
            className="recap-minimal-performer-box"
            style={{
              background:
                textColor === '#ffffff'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.08)',
              border:
                textColor === '#ffffff'
                  ? '1px solid rgba(255, 255, 255, 0.15)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="recap-minimal-performer-label">
              ⭐ PLAYER OF THE GAME
            </div>
            <div className="recap-minimal-performer-name">{topPerformer}</div>
            {topPerformerStats && (
              <div className="recap-minimal-performer-stats">
                {topPerformerStats}
              </div>
            )}
          </div>
        )}

        {gameDate && (
          <div className="recap-minimal-date">{formatGameDate(gameDate, dateFormat)}</div>
        )}
      </div>

      {showWatermark && (
        <div className="playmaker-watermark">
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" />
        </div>
      )}
    </div>
  );
}

export default GraphicRecapMinimal;
